import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader } from '~/client/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/client/components/ui/dialog'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '~/client/components/ui/input-otp'
import { toast } from 'sonner'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { resetPassword } from '~/server/common/supabase/auth'
import { supabaseClient } from '~/server/common/supabase/client'
import { RevelStreetLogoHeaderIcon } from '~/client/components/icons/RevelStreetLogoHeaderIcon'
import { fireCustomerIOEvent } from '~/client/common/customerio'
import { CustomerIOEventName } from '~/shared/customerio/customerio.types'
import { AgreementCheckbox } from '~/client/components/agreement/AgreementCheckbox'
import { activeAgreementsQuery } from '~/client/agreement/agreement.queries'
import { useCreateConsents } from '~/client/agreement/agreement.hooks'
import { REQUIRED_AGREEMENT_TYPES } from '~/shared/agreement/agreement.constants'
import { AGREEMENT_CONSENT_ENABLED } from '~/client/common/envvars'

export const Route = createFileRoute('/onboarding')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || undefined,
    }
  },
})

// Define Zod schema with proper validation
const onboardingSchema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    agreementsAccepted: AGREEMENT_CONSENT_ENABLED
      ? z.boolean().refine((v) => v === true, {
          message: 'You must accept the agreements to continue',
        })
      : z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type OnboardingValues = z.infer<typeof onboardingSchema>

function RouteComponent() {
  const navigate = useNavigate()
  const { token } = Route.useSearch()
  const [userEmail, setUserEmail] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [showOtpDialog, setShowOtpDialog] = useState(false)
  const [otpStep, setOtpStep] = useState<'email' | 'verify'>('email')
  const [otpEmail, setOtpEmail] = useState('')
  const [otpToken, setOtpToken] = useState('')
  const [otpError, setOtpError] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [redirecting, setRedirecting] = useState(false)

  // Use a ref to ensure verification only happens once
  // This prevents double-fetching in React Strict Mode (development)
  const verificationAttemptedRef = useRef(false)

  useEffect(() => {
    // When the invite link still carries the raw token, DON'T consume it on
    // load. Email security scanners (Proofpoint, Microsoft Safe Links, etc.)
    // pre-fetch links and execute page JS, which would burn the single-use
    // Supabase invite token before the human ever clicks. Defer consumption to
    // an explicit user action (handleStartSetup); just render the gate screen.
    if (token) {
      setIsLoading(false)
      return
    }

    const checkSession = async () => {
      // Prevent multiple verification attempts (React Strict Mode double-invoke)
      if (verificationAttemptedRef.current) {
        return
      }
      verificationAttemptedRef.current = true

      try {
        // Check if user is authenticated (after being redirected back from Supabase)
        // The Supabase client automatically handles hash parameters from the URL
        const {
          data: { session },
        } = await supabaseClient.auth.getSession()

        if (session?.user) {
          // User is authenticated via the hash parameters from Supabase redirect
          setUserEmail(session.user.email || '')
          setIsLoading(false)
        } else {
          // No session - show OTP dialog as fallback
          setIsLoading(false)
          setShowOtpDialog(true)
        }
      } catch (error) {
        console.error('Onboarding verification error:', error)
        // Show OTP dialog as fallback instead of redirecting away
        setIsLoading(false)
        setShowOtpDialog(true)
      }
    }

    checkSession()
  }, [token])

  // Consume the invite token only on explicit user action. This redirect is the
  // single point where the single-use Supabase token gets burned; gating it
  // behind a click is what defeats scanner pre-fetch (see useEffect above).
  const handleStartSetup = () => {
    if (!token || redirecting) return

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    if (!supabaseUrl) {
      console.error('Missing VITE_SUPABASE_URL environment variable')
      toast.error('Something went wrong. Please try again.', {
        position: 'bottom-right',
        richColors: true,
      })
      return
    }

    // Disable the button and show a spinner while the navigation commits — on a
    // slow network the redirect isn't instant, so give the user feedback and
    // prevent a second click from re-firing the redirect.
    setRedirecting(true)

    const redirectTo = `${window.location.origin}/onboarding`
    const verifyUrl = `${supabaseUrl}/auth/v1/verify?token=${encodeURIComponent(token)}&type=invite&redirect_to=${encodeURIComponent(redirectTo)}`

    // Supabase verifies the token then redirects back to /onboarding (no token,
    // session in the URL hash), which falls through to the password-form path.
    window.location.href = verifyUrl
  }

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<OnboardingValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      agreementsAccepted: false,
    },
  })

  const { data: activeAgreements } = useQuery({ ...activeAgreementsQuery(), enabled: AGREEMENT_CONSENT_ENABLED })
  const createConsents = useCreateConsents()

  const handleSendOtp = async (email: string) => {
    setOtpLoading(true)
    setOtpError('')
    try {
      const { error } = await supabaseClient.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      })
      if (error) {
        setOtpError(error.message)
        return
      }
      setOtpStep('verify')
      toast.success('Check your email for a verification code', {
        position: 'bottom-right',
        richColors: true,
      })
    } catch {
      setOtpError('Something went wrong. Please try again.')
    } finally {
      setOtpLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    setOtpLoading(true)
    setOtpError('')
    try {
      const { data, error } = await supabaseClient.auth.verifyOtp({
        email: otpEmail,
        token: otpToken,
        type: 'email',
      })
      if (error) {
        setOtpError('Invalid or expired code. Please try again.')
        return
      }
      if (data.session?.user) {
        const verifiedEmail = data.session.user.email || otpEmail
        setUserEmail(verifiedEmail)
        setValue('email', verifiedEmail)
        setShowOtpDialog(false)
        toast.success('Email verified successfully', {
          position: 'bottom-right',
          richColors: true,
        })
      }
    } catch {
      setOtpError('Something went wrong. Please try again.')
    } finally {
      setOtpLoading(false)
    }
  }

  const handleResendOtp = () => {
    setOtpToken('')
    setOtpError('')
    handleSendOtp(otpEmail)
  }

  const handleChangeEmail = () => {
    setOtpStep('email')
    setOtpToken('')
    setOtpError('')
  }

  const onSubmit = async (values: OnboardingValues) => {
    if (values.email.toLowerCase() !== userEmail.toLowerCase()) {
      setError('email', {
        type: 'manual',
        message: 'Email does not match the invite link',
      })
      return
    }

    try {
      // Set the user's password
      await resetPassword(values.password)

      // Record consent for the latest active versions of all required
      // agreements. Failure here is non-fatal: the _app gate will catch any
      // missing consents on the next page load and prompt again.
      if (AGREEMENT_CONSENT_ENABLED && activeAgreements && activeAgreements.length > 0) {
        try {
          await createConsents.mutateAsync({
            agreement_version_ids: activeAgreements.map((v) => v.id),
            consent_url: '/onboarding',
          })
        } catch (err) {
          console.error('[onboarding] Failed to record agreement consent', err)
        }
      }

      fireCustomerIOEvent(CustomerIOEventName.UserFirstSignedIn)

      toast.success("You're in! Let's start finding the perfect venue.", {
        position: 'bottom-right',
        richColors: true,
        duration: 3000,
      })

      // Navigate to the main app
      setTimeout(() => {
        navigate({ to: '/' })
      }, 500)
    } catch (err: any) {
      const message = err?.message || 'Failed to set up your account'
      toast.error(message, {
        position: 'bottom-right',
        richColors: true,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="p-8">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#8A3DFF] border-t-transparent" />
                <p className="text-sm text-gray-600">Verifying your invite...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Invite-token gate: render a click-to-continue screen instead of the password
  // form. The token is consumed only when the user clicks (handleStartSetup),
  // so scanners that load this page without clicking never burn it.
  if (token) {
    return (
      <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              {/* Width must be set on the SVG itself (it has a viewBox, no intrinsic width),
                  otherwise it collapses to zero — mirror the login page's logo treatment. */}
              <RevelStreetLogoHeaderIcon className="h-12 w-auto text-[#18003D] mx-auto mb-2" />
              <CardDescription className="text-gray-600">You're one step away. Create your account to start sourcing venues on Revel Street.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button type="button" className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]" onClick={handleStartSetup} disabled={redirecting}>
                {redirecting ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Creating your account...
                  </span>
                ) : (
                  'Create my account'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            {/* Width must be set on the SVG itself (it has a viewBox, no intrinsic width),
                otherwise it collapses to zero — mirror the login page's logo treatment. */}
            <RevelStreetLogoHeaderIcon className="h-12 w-auto text-[#18003D] mx-auto mb-2" />
            <CardDescription className="text-gray-600">Complete your account setup by creating a password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
              {/* Email Field */}
              <div className="grid gap-3">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="Enter your email" {...register('email')} aria-invalid={errors.email ? 'true' : 'false'} />
                {errors.email && <span className="text-sm text-destructive">{errors.email.message}</span>}
              </div>

              {/* Password Field */}
              <div className="grid gap-3">
                <Label htmlFor="password">Create Password</Label>
                <Input id="password" type="password" placeholder="Enter your password" {...register('password')} aria-invalid={errors.password ? 'true' : 'false'} />
                {errors.password && <span className="text-sm text-destructive">{errors.password.message}</span>}
              </div>

              {/* Confirm Password Field */}
              <div className="grid gap-3">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input id="confirmPassword" type="password" placeholder="Re-enter your password" {...register('confirmPassword')} aria-invalid={errors.confirmPassword ? 'true' : 'false'} />
                {errors.confirmPassword && <span className="text-sm text-destructive">{errors.confirmPassword.message}</span>}
              </div>

              {/* Password Requirements */}
              <div className="rounded-md bg-blue-50 p-3">
                <p className="text-xs text-blue-800 font-medium mb-1">Password Requirements:</p>
                <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                  <li>At least 8 characters long</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                </ul>
              </div>

              {/* Agreements Acceptance */}
              {AGREEMENT_CONSENT_ENABLED && (
                <Controller
                  control={control}
                  name="agreementsAccepted"
                  render={({ field, fieldState }) => (
                    <AgreementCheckbox
                      id="agreementsAccepted"
                      types={REQUIRED_AGREEMENT_TYPES}
                      checked={field.value === true}
                      onCheckedChange={field.onChange}
                      error={fieldState.error?.message}
                      textClassName="text-xs leading-4"
                    />
                  )}
                />
              )}

              {/* Submit Button */}
              <Button type="submit" className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Setting up your account...
                  </span>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* OTP Verification Dialog */}
      <Dialog open={showOtpDialog} onOpenChange={(open) => {
        if (!open && !userEmail) {
          // Don't allow closing if user hasn't verified yet
          return
        }
        setShowOtpDialog(open)
      }}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => { if (!userEmail) e.preventDefault() }} onEscapeKeyDown={(e) => { if (!userEmail) e.preventDefault() }}>
          {otpStep === 'email' ? (
            <>
              <DialogHeader>
                <DialogTitle className="mb-2">Verify your email</DialogTitle>
                <DialogDescription>
                  For your security, invite links can only be used once. To continue setting up your account, enter your email below to receive a one-time code.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="otp-email">Email</Label>
                  <Input
                    id="otp-email"
                    type="email"
                    placeholder="Enter your email"
                    value={otpEmail}
                    onChange={(e) => {
                      setOtpEmail(e.target.value)
                      setOtpError('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && otpEmail) {
                        e.preventDefault()
                        handleSendOtp(otpEmail)
                      }
                    }}
                  />
                </div>
                {otpError && <p className="text-sm text-destructive">{otpError}</p>}
                <Button
                  className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]"
                  disabled={otpLoading || !otpEmail}
                  onClick={() => handleSendOtp(otpEmail)}
                >
                  {otpLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Sending code...
                    </span>
                  ) : (
                    'Send Code'
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Enter verification code</DialogTitle>
                <DialogDescription>
                  We sent a 6-digit code to <span className="font-medium text-foreground">{otpEmail}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpToken}
                    onChange={(value) => {
                      setOtpToken(value)
                      setOtpError('')
                    }}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {otpError && <p className="text-sm text-destructive text-center">{otpError}</p>}
                <Button
                  className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]"
                  disabled={otpLoading || otpToken.length !== 6}
                  onClick={handleVerifyOtp}
                >
                  {otpLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Verifying...
                    </span>
                  ) : (
                    'Verify'
                  )}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-[#8A3DFF] hover:underline"
                    onClick={handleResendOtp}
                    disabled={otpLoading}
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    className="text-[#8A3DFF] hover:underline"
                    onClick={handleChangeEmail}
                    disabled={otpLoading}
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
